import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  calculateLeaveRequest,
  submitLeaveRequest,
  getLeaveRequest,
  getMyLeaveRequests,
  getLeaveReviewQueue,
  getLeaveRequestHistory,
  approveLeaveRequest,
  rejectLeaveRequest,
  overrideLeaveRequest,
  cancelLeaveRequest,
  submitCancellationRequest,
  getCancellationRequest,
  getCancellationReviewQueue,
  approveCancellationRequest,
  rejectCancellationRequest,
} from '../api/leaveRequest';
import {
  SubmitLeaveRequestPayload,
  CalculateLeaveRequestPayload,
  OverrideDecision,
  LeaveListParams,
  ReviewQueueParams,
  LeaveHistoryParams,
} from 'types/leaveRequest';
import { MY_LEAVE_BALANCES_QUERY_KEY } from './leaveBalanceHooks';

export const LEAVE_REQUEST_QUERY_KEY = ['leaveRequest'];
export const MY_LEAVE_REQUESTS_QUERY_KEY = ['myLeaveRequests'];
export const LEAVE_REVIEW_QUEUE_QUERY_KEY = ['leaveReviewQueue'];
export const LEAVE_REQUEST_HISTORY_QUERY_KEY = ['leaveRequestHistory'];
export const LEAVE_CANCELLATION_REQUEST_QUERY_KEY = ['leaveCancellationRequest'];
export const CANCELLATION_REVIEW_QUEUE_QUERY_KEY = ['cancellationReviewQueue'];

export const useCalculateLeaveRequestMutation = () =>
  useMutation({
    mutationFn: (data: CalculateLeaveRequestPayload) => calculateLeaveRequest(data),
  });

export const useLeaveRequestQuery = (uuid?: string) =>
  useQuery({
    queryKey: [...LEAVE_REQUEST_QUERY_KEY, uuid],
    queryFn: () => getLeaveRequest(uuid as string),
    enabled: !!uuid,
  });

// Serves both Pending Requests (pass status) and Leave History (omit status)
// — see getMyLeaveRequests for the exact query-param contract.
export const useMyLeaveRequestsQuery = (params: LeaveListParams = {}) =>
  useQuery({
    queryKey: [...MY_LEAVE_REQUESTS_QUERY_KEY, params],
    queryFn: () => getMyLeaveRequests(params),
  });

// The HR/approver review screen's data source — only rows the viewer can
// currently action. Refetch after every approve/reject/override so decided
// items disappear.
export const useLeaveReviewQueueQuery = (params: ReviewQueueParams = {}) =>
  useQuery({
    queryKey: [...LEAVE_REVIEW_QUEUE_QUERY_KEY, params],
    queryFn: () => getLeaveReviewQueue(params),
    placeholderData: keepPreviousData,
  });

// The Admin History tab's data source — decided requests, separate from the
// review queue (which only ever returns still-actionable pending items).
export const useLeaveRequestHistoryQuery = (params: LeaveHistoryParams = {}) =>
  useQuery({
    queryKey: [...LEAVE_REQUEST_HISTORY_QUERY_KEY, params],
    queryFn: () => getLeaveRequestHistory(params),
    placeholderData: keepPreviousData,
  });

export const useSubmitLeaveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitLeaveRequestPayload) => submitLeaveRequest(data),
    onSuccess: () => {
      // Balance is reserved at successful submission — refresh the self-view
      // cards and the requester's own lists so the new request shows up.
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_BALANCES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_REQUESTS_QUERY_KEY });
    },
  });
};

export const useApproveLeaveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, version, reason }: { uuid: string; version: number; reason?: string | null }) =>
      approveLeaveRequest(uuid, version, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: LEAVE_REVIEW_QUEUE_QUERY_KEY });
    },
  });
};

export const useRejectLeaveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, version, reason }: { uuid: string; version: number; reason?: string | null }) =>
      rejectLeaveRequest(uuid, version, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: LEAVE_REVIEW_QUEUE_QUERY_KEY });
    },
  });
};

export const useOverrideLeaveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uuid,
      version,
      decision,
      reason,
    }: {
      uuid: string;
      version: number;
      decision: OverrideDecision;
      reason: string;
    }) => overrideLeaveRequest(uuid, { version, decision, reason }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: LEAVE_REVIEW_QUEUE_QUERY_KEY });
    },
  });
};

export const useCancelLeaveRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, version, reason }: { uuid: string; version: number; reason?: string | null }) =>
      cancelLeaveRequest(uuid, version, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_BALANCES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LEAVE_REVIEW_QUEUE_QUERY_KEY });
    },
  });
};

export const useSubmitCancellationRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      leaveRequestUuid,
      version,
      reason,
    }: {
      leaveRequestUuid: string;
      version: number;
      reason: string;
    }) => submitCancellationRequest(leaveRequestUuid, { version, reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, variables.leaveRequestUuid] });
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_REQUESTS_QUERY_KEY });
    },
  });
};

export const useCancellationRequestQuery = (uuid?: string) =>
  useQuery({
    queryKey: [...LEAVE_CANCELLATION_REQUEST_QUERY_KEY, uuid],
    queryFn: () => getCancellationRequest(uuid as string),
    enabled: !!uuid,
  });

// The HR Admin cancellation-review screen's data source.
export const useCancellationReviewQueueQuery = (params: ReviewQueueParams = {}) =>
  useQuery({
    queryKey: [...CANCELLATION_REVIEW_QUEUE_QUERY_KEY, params],
    queryFn: () => getCancellationReviewQueue(params),
  });

export const useApproveCancellationRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, version, reason }: { uuid: string; version: number; reason?: string | null }) =>
      approveCancellationRequest(uuid, version, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_CANCELLATION_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.leaveRequestUuid] });
      queryClient.invalidateQueries({ queryKey: CANCELLATION_REVIEW_QUEUE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_BALANCES_QUERY_KEY });
      // The leave itself flips to Cancelled, so every list showing it is stale.
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LEAVE_REVIEW_QUEUE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LEAVE_REQUEST_HISTORY_QUERY_KEY });
    },
  });
};

export const useRejectCancellationRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, version, reason }: { uuid: string; version: number; reason?: string | null }) =>
      rejectCancellationRequest(uuid, version, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...LEAVE_CANCELLATION_REQUEST_QUERY_KEY, data.uuid] });
      queryClient.invalidateQueries({ queryKey: [...LEAVE_REQUEST_QUERY_KEY, data.leaveRequestUuid] });
      queryClient.invalidateQueries({ queryKey: CANCELLATION_REVIEW_QUEUE_QUERY_KEY });
      // The leave's "Cancellation Pending" badge becomes "Cancellation Rejected".
      queryClient.invalidateQueries({ queryKey: MY_LEAVE_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LEAVE_REQUEST_HISTORY_QUERY_KEY });
    },
  });
};
